/**
 * @file Select — 二次封装的下拉选择组件
 * @description 基于 antd Select 封装，支持分组、图标、单选/多选、hover 态等
 */

import {useMemo, useCallback, type MouseEvent} from 'react';
import {Select as AntdSelect} from 'antd';
import type {SelectProps} from 'antd';
import {cx, mergeSemanticClassNames} from '@utils/cx';
import styles from './index.module.less';
import Icon from '@/components/common/Icon';
import type {IconName} from '@/components/common/Icon';
import {DiamondCtaBadge, DiamondIcon, PaywallTooltip} from '@/commerce/paywall';
import type {MemberOptionMeta} from '@/types/generator';

export type SelectValue = string | number;

export interface SelectOption extends MemberOptionMeta {
    /**
     * 选项值
     */
    value: SelectValue;

    /**
     * 选项显示文本
     */
    label: string;

    /**
     * 是否禁用
     */
    disabled?: boolean;

    /**
     * 描述信息
     */
    description?: string;

    /**
     * 标签
     * 如「推荐」「新」
     */
    tag?: string;

    /**
     * 缩略图 URL
     */
    thumbnail?: string;

    /**
     * 左侧图标名称（Icon 组件 name）
     */
    icon?: IconName;
}

/**
 * 分组选项
 */
export interface SelectOptionGroup {
    /**
     * 分组标题
     */
    label: string;

    /**
     * 分组下的选项
     */
    options: SelectOption[];
}

export type SelectSize = 'large' | 'middle' | 'small';

export type CustomSelectProps<TValue extends SelectValue | SelectValue[] = SelectValue> = Omit<
    SelectProps<TValue>,
    'options' | 'size'
> & {
    /**
     * 下拉选项列表。支持扁平结构或分组结构
     */
    options: SelectOption[] | SelectOptionGroup[];

    /**
     * 尺寸控制高度
     * - large: 40px (默认)
     * - middle: 36px
     * - small: 32px
     */
    size?: SelectSize;
};

/**
 * 判断是否是分组形式的 options
 * 空数组视为扁平结构
 */
function isGroupedOptions(
    options: SelectOption[] | SelectOptionGroup[],
): options is SelectOptionGroup[] {
    const first = options[0];
    return first != null && 'options' in first && Array.isArray(first.options);
}

const Select = <TValue extends SelectValue | SelectValue[] = SelectValue>({
    options,
    className,
    classNames,
    value,
    mode,
    size = 'large',
    variant = 'outlined',
    ...rest
}: CustomSelectProps<TValue>) => {
    // 将分组结构扁平化为 value → option 的映射，便于查找
    const optionMap = useMemo(() => {
        const map = new Map<string, SelectOption>();
        if (isGroupedOptions(options)) {
            options.forEach((g) => {
                g.options.forEach((opt) => {
                    map.set(String(opt.value), opt);
                });
            });
        } else {
            options.forEach((opt) => {
                map.set(String(opt.value), opt);
            });
        }

        return map;
    }, [options]);

    const mergedClassNames = useMemo(
        () => mergeSemanticClassNames({popup: {root: styles.selectDropdown}}, classNames),
        [classNames],
    );

    // 转换为 antd Select 可接受的 options 结构
    const antdOptions = useMemo(() => {
        const toAntdOpt = (opt: SelectOption) => ({
            value: opt.value,
            label: opt.label,
            disabled: opt.disabled,
            title: opt.description,
        });

        if (isGroupedOptions(options)) {
            return options.map((group) => ({
                label: group.label,
                title: group.label,
                options: group.options.map(toAntdOpt),
            }));
        }
        return options.map(toAntdOpt);
    }, [options]);

    const isMultiple = mode === 'multiple' || mode === 'tags';

    const stopOptionAction = useCallback((e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const labelRender = useCallback(
        (props: {label?: React.ReactNode; value?: string | number}) => {
            const opt = optionMap.get(String(props.value));
            return (
                <span className={styles.selectLabel}>
                    {opt?.icon && (
                        <span className={styles.selectLabelIcon}>
                            <Icon name={opt.icon} size={16} />
                        </span>
                    )}
                    <span className={styles.selectLabelText}>{props.label}</span>
                </span>
            );
        },
        [optionMap],
    );

    const optionRender = useCallback(
        (option: {value?: string | number; label?: React.ReactNode}) => {
            const opt = optionMap.get(String(option.value));
            if (!opt) {
                return option.label;
            }

            const isSelected = Array.isArray(value)
                ? value.map(String).includes(String(option.value))
                : value != null && String(option.value) === String(value);

            const {
                thumbnail,
                icon,
                label,
                tag,
                memberOnly,
                memberLocked,
                memberTooltip,
                memberAction,
            } = opt;
            const hasLeading = Boolean(thumbnail || icon);
            const hasMemberMeta = memberOnly || Boolean(memberAction) || Boolean(tag);
            const content = (
                <span
                    className={`${styles.optionContent}${thumbnail ? ` ${styles.optionContentWithThumbnail}` : ''}${memberLocked ? ` ${styles.optionContentLocked}` : ''}`}
                    onMouseDown={memberLocked ? stopOptionAction : undefined}
                    onClick={memberLocked ? stopOptionAction : undefined}
                >
                    {thumbnail && (
                        <span className={styles.optionThumbnail}>
                            <img className={styles.optionThumbnailImg} src={thumbnail} alt="" />
                        </span>
                    )}

                    {!thumbnail && icon && (
                        <span className={styles.optionIcon}>
                            <Icon name={icon} size={16} />
                        </span>
                    )}

                    <span
                        className={`${styles.optionText} ${hasLeading ? '' : styles.optionTextPlain} ${hasMemberMeta ? styles.optionTextWithMeta : ''}`}
                    >
                        <span
                            className={`${styles.optionLabel} ${memberLocked ? styles.optionLabelMuted : ''}`}
                        >
                            {label}
                        </span>
                        {hasMemberMeta && (
                            <span className={styles.optionMeta}>
                                {memberOnly && !memberAction && (
                                    <DiamondIcon size={16} className={styles.optionMemberIcon} />
                                )}
                                {memberAction && (
                                    <DiamondCtaBadge
                                        className={styles.optionMemberAction}
                                        text={memberAction.text}
                                        onClick={memberAction.onClick}
                                    />
                                )}
                                {tag && <span className={styles.optionTag}>{tag}</span>}
                            </span>
                        )}
                    </span>

                    {isSelected && !memberLocked && (
                        <span className={styles.optionCheck}>
                            <Icon name="AijhBasicDuihao" size={18} />
                        </span>
                    )}
                </span>
            );

            if (!memberTooltip) {
                return content;
            }

            return <PaywallTooltip hint={memberTooltip}>{content}</PaywallTooltip>;
        },
        [optionMap, value, stopOptionAction],
    );

    return (
        <AntdSelect<TValue>
            variant={variant}
            showSearch={false}
            virtual={false} // 默认关掉虚拟滚动
            placement="topLeft"
            mode={mode}
            suffixIcon={<Icon name="AijBasicXia" size={16} className={styles.chevronIcon} />}
            menuItemSelectedIcon={null}
            className={cx(styles.select, styles[size], className)}
            classNames={mergedClassNames}
            labelRender={isMultiple ? undefined : labelRender}
            options={antdOptions}
            optionRender={optionRender}
            value={value}
            {...rest}
        />
    );
};

export default Select;
